/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Crystal-side MD integrator. Cloned from src/models/MolecularDynamics.ts
 * so we can iterate on PBC / Ewald / Buckingham without disturbing the
 * shipping molecule MD path.
 *
 * Differences from MolecularDynamics:
 *   - Takes a flat `atoms: Atom[]` plus a container instead of a
 *     `molecules: Molecule[]` array. Crystals don't have the molecule
 *     wrapper concept; the supercell's expanded atom set is the unit.
 *   - No radialBonds / angularBonds / torsionalBonds fields. Crystal
 *     "bonds" come from the lattice + pair potential, not a bond list.
 *   - calculateForce() only sums pair interactions + restraints +
 *     external fields. The molecule-side bonded-force loops are gone.
 *   - applyBoundary() stays as reflecting walls for now. PBC will
 *     replace it in a follow-up PR.
 *
 * Reused verbatim: the velocity-Verlet predict/correct scheme (delegated
 * to Atom.predict / Atom.correct), the heat bath rescaling, the
 * temperature / pressure / density readouts, and the gravitational
 * external-field hook (for parity with the molecule chamber's UI; we
 * may drop it from the crystal toolbar later since it's not physically
 * meaningful for a supercell).
 */

import { Atom } from '../Atom.ts';
import { MolecularContainer } from '../../types.ts';
import { CrystalPairInteractions } from './CrystalPairInteractions.ts';
import { RadialBond } from '../RadialBond.ts';
import { AngularBond } from '../AngularBond.ts';
import { TorsionalBond } from '../TorsionalBond.ts';
import { VirtualSite } from './VirtualSite.ts';
import { ConstraintSet } from './RigidConstraints.ts';
import { PeriodicBox } from './PeriodicBox.ts';
import { ThermostatType, csvrNewKineticEnergy } from './Thermostat.ts';
import { ResolvedThermostatGroup, groupTemperature } from '../thermostatGroups.ts';
import {
  DENSITY_CONVERSION_CONSTANT,
  EV_CONVERTER,
  GF_CONVERSION_CONSTANT,
  GRAVITY_CONVERSION_CONSTANT,
  PRESSURE_CONVERSION_CONSTANT,
  UNIT_EV_OVER_KB,
  VIRIAL_CONVERSION_CONSTANT,
  VT_CONVERSION_CONSTANT,
} from '../physicalConstants.ts';
import { ZERO_TOLERANCE } from '../../constants.ts';
import { ModelUtil } from '../ModelUtil.ts';
import { HeatBath } from '../HeatBath.ts';
import { ExternalField, ExternalFieldType } from '../ExternalField.ts';
import { Vector3 } from 'three';

/**
 * Convergence tests for `CrystalDynamics.minimize`. The defaults suit the
 * relaxation a crystal gets when it enters the lab; a protocol that compares
 * energies between geometries (the tensile test, sub-meV/atom differences)
 * passes tighter values.
 */
export interface MinimizeOptions {
  /** Stop when the largest force is below this, eV/Å (default 0.01). */
  forceTol?: number;
  /** Stop when the best energy improves by less than this per movable atom per step, eV (default 5e-6). */
  stallTol?: number;
}

export class CrystalDynamics {
  atoms: Atom[];
  pairInteractions: CrystalPairInteractions;
  /**
   * Intramolecular bonds. Required for molecular crystals — without
   * these the LJ pair potential lets covalent skeletons dissociate at
   * the first thermal kick. Empty for ionic / metallic crystals where
   * "bonds" are an emergent feature of the pair potential + Coulomb,
   * not discrete entities. Populated from the supercell builder before
   * starting MD.
   */
  radialBonds: RadialBond[];
  angularBonds: AngularBond[];
  torsionalBonds: TorsionalBond[];
  /**
   * Virtual interaction sites (e.g. the M-site of TIP4P-family water
   * models). Mass-less, charge-bearing points whose positions are
   * linear combinations of parent atoms. The pair kernel sweeps them
   * in addition to real atoms during the Coulomb pass; the integrator
   * doesn't move them directly — `move()` recomputes their positions
   * from the parents after the predict step.
   */
  virtualSites: VirtualSite[] = [];
  /**
   * Rigid intramolecular constraints (SHAKE/RATTLE). When set, the
   * molecules carrying these constraints are integrated as rigid bodies
   * — the harmonic bonded-force loops are skipped for them and `move()`
   * uses the constrained velocity-Verlet path. This is mandatory for
   * water: a flexible point-charge water model collapses (a hydrogen is
   * pulled off its oxygen by a neighbor's charge). Undefined / empty
   * for crystals integrated with the flexible (harmonic) path.
   */
  constraints?: ConstraintSet;
  /** Scratch buffer: pre-drift positions, the SHAKE reference frame. */
  private prevPositions: Vector3[] = [];
  /**
   * Periodic box. When set, the crystal is simulated under periodic
   * boundary conditions — the supercell tiles infinitely, so there is
   * no surface and the lattice is the true energy minimum. The
   * reflecting-wall `applyBoundary` is skipped; minimum-image in the
   * pair kernel handles wrap-around.
   */
  box?: PeriodicBox;
  /**
   * Molecular-species tag (e.g. 'water'). Set by the supercell builder
   * after topology detection — readers (simulation controls, agent
   * tools) can branch on this without re-doing the detection.
   * Undefined for non-molecular crystals.
   */
  species?: string;
  externalFields: ExternalField[];
  container: MolecularContainer;
  potentialEnergy: number;
  kineticEnergy: number;
  totalEnergy: number;
  movableCount: number;
  gravitationDirection: Vector3;

  timeStep: number = 0.5;
  indexOfStep: number = 0;
  /**
   * Set true when the user manually edits the live atom set (cluster
   * translate / rotate / restrain / cut). The persistence layer captures a
   * snapshot whenever this is set OR the engine has stepped, so lab edits
   * made before running MD aren't lost on save/reload.
   */
  edited: boolean = false;
  /**
   * Makes minimize() a no-op. Set by configureCrystalDynamics only while the
   * force field configures an engine that is about to be overwritten by a saved
   * snapshot — relaxing a lattice whose every coordinate is then replaced cost
   * seconds on open for EAM copper and Ewald salt.
   */
  skipMinimize: boolean = false;
  /**
   * The store objects (crystal, supercell, guests) the on-screen mirror was
   * built from. The simulation controls drop a worker packet when the engine
   * that produced it was built from different ones — a same-size frame of the
   * previous project, copied by index, would draw the wrong structure.
   */
  builtFrom?: { crystal: unknown; supercell: unknown; guests: unknown };
  heatBath: HeatBath;
  /**
   * Berendsen thermostat coupling time in fs. The molecule MD uses an
   * effectively instant rescale (ratio = sqrt(T_target/T)), which is
   * the τ → 0 limit. That keeps `<KE>` rigidly at the target but is
   * known to redistribute energy non-physically across modes — the
   * "flying ice cube" effect, where translation/rotation modes
   * dominate and intramolecular vibrations are suppressed. For
   * molecular crystals (water especially) that shows up as molecules
   * spinning violently around their centers while the H-O-H bend
   * stays cold. A τ ~100 fs gives a soft Berendsen rescale that
   * lets modes equilibrate naturally over a few ps.
   */
  thermostatTau: number = 100;
  /**
   * Which thermostat regulates the temperature when the heat bath is
   * on. 'berendsen' (default) is the weak-coupling rescale; 'csvr' adds
   * the stochastic term that restores the correct canonical kinetic-
   * energy fluctuations. See Thermostat.ts.
   */
  thermostat: ThermostatType = 'berendsen';
  /**
   * Thermostat GROUPS (see models/thermostatGroups.ts): sets of atoms held
   * at their own setpoints with the lab's thermostat algorithm, applied to
   * those atoms only. While any group is set, the global heat bath acts
   * only on the atoms in no group (NVE for them when it is off). Set via
   * setThermostatGroups with membership already resolved to indices.
   */
  thermostatGroups: ResolvedThermostatGroup[] = [];
  /** Atoms in no group — the global thermostat's domain while groups exist. */
  private ungroupedIndices: number[] = [];
  /**
   * Energy each group's thermostat has ADDED to its atoms since the group was
   * set (eV; negative = removed), by group id. The heat current in a
   * conduction experiment is read from here: what the hot bath puts in and
   * the cold bath takes out. Kept across the per-chunk re-resolution of
   * membership (same ids), dropped when a group disappears. The worker ships
   * it in every packet; the main-thread mirror only receives it.
   */
  thermostatGroupHeatById = new Map<string, number>();

  constructor(
    atoms: Atom[],
    container: MolecularContainer,
    radialBonds: RadialBond[] = [],
    angularBonds: AngularBond[] = [],
    torsionalBonds: TorsionalBond[] = [],
  ) {
    this.atoms = atoms;
    this.radialBonds = radialBonds;
    this.angularBonds = angularBonds;
    this.torsionalBonds = torsionalBonds;
    this.pairInteractions = new CrystalPairInteractions(this.atoms, radialBonds);
    this.pairInteractions.virtualSites = this.virtualSites;
    this.externalFields = [];
    this.container = { ...container };
    this.potentialEnergy = 0;
    this.kineticEnergy = 0;
    this.totalEnergy = 0;
    this.movableCount = 0;
    this.heatBath = new HeatBath(300);
    this.gravitationDirection = new Vector3(0, 0, -1);
  }

  /**
   * Switch the engine to periodic boundary conditions over `box`
   * (the crystal supercell). Forwards the box to the pair kernel,
   * which then uses minimum-image distances and damped-shifted-force
   * electrostatics.
   */
  setPeriodicBox(box: PeriodicBox): void {
    this.box = box;
    this.pairInteractions.setPeriodicBox(box);
    // Under PBC the simulation cell IS the container: the user's container
    // setting has no physical meaning here (no walls, see applyBoundary),
    // and volume / density / pressure must be per cell, not per some
    // unrelated box. So the container yields to the periodic cell — the
    // viewer draws this box, and the molecule-lab container size is ignored
    // for as long as the box is installed.
    this.container = { ...this.container, ...box.extents() };
  }

  countMovables(): number {
    this.movableCount = 0;
    for (const a of this.atoms) {
      if (!a.fixed) this.movableCount++;
    }
    return this.movableCount;
  }

  reset(): void {
    this.indexOfStep = 0;
    for (const a of this.atoms) {
      a.reset();
    }
    // Atoms jump back to their initial positions — the neighbor list
    // built from the pre-reset geometry is now stale; force a rebuild.
    this.pairInteractions.invalidateNeighborList();
  }

  init(): void {
    for (const a of this.atoms) {
      a.setRandomVelocity(0.1);
    }
  }

  /**
   * Steepest-descent energy minimization. MD must never start from an
   * unrelaxed structure. An AI-generated crystal — or any geometry not
   * native to this exact force field — sits some way up the potential
   * surface. Velocity-Verlet at temperature converts that stored
   * potential energy into a burst of kinetic energy far faster than the
   * thermostat can drain it: the crystal melts during the relaxation
   * burst, and the thermostat then merely cools an already-disordered
   * liquid back to the set point. (That is exactly the "starts ordered,
   * then melts at 50 K" symptom.) Draining the excess at zero
   * temperature here, before any dynamics, is what lets a cold crystal
   * stay crystalline.
   *
   * FIRE (fast inertial relaxation engine, Bitzek et al. 2006): a damped
   * dynamics in which each atom carries a minimizer velocity, the velocity is
   * steered toward the force every step, the time step grows while the power
   * F·v stays positive and is halved (with the velocity zeroed and a half step
   * undone) when the motion turns uphill. It relaxes collective soft modes —
   * a nanotube wall expanding to its force field's bond length, a lattice
   * settling to its equilibrium constant — in tens of steps where capped
   * steepest descent, the previous method, was still creeping after 300.
   * `atom.force` holds accelerations (F·GF/m), so the velocities and the step
   * are in the integrator's own Å / fs units and the mass weighting FIRE
   * prescribes is already in the force. A per-atom displacement cap keeps one
   * bad contact from flinging an atom across the box.
   *
   * Converged when the largest force is below FIRE_FORCE_TOL (eV/Å), or when
   * the best energy is improving by less than FIRE_STALL_TOL per atom per step
   * over the last FIRE_STALL_WINDOW steps while the largest force is below
   * FIRE_FORCE_LOOSE — the strain still stored is then about a millielectronvolt
   * per atom, a kelvin or two of heating when the run starts. `maxSteps` stays
   * the hard cap. Returns the number of steps taken.
   */
  minimize(maxSteps: number = 300, maxDisplacement: number = 0.05, opts: MinimizeOptions = {}): number {
    if (this.skipMinimize || this.atoms.length === 0) return 0;
    this.countMovables();
    if (this.movableCount === 0) return 0;
    const forceTol = opts.forceTol ?? CrystalDynamics.FIRE_FORCE_TOL;
    const stallTol = opts.stallTol ?? CrystalDynamics.FIRE_STALL_TOL;
    const stallWindow = CrystalDynamics.FIRE_STALL_WINDOW;
    const n = this.atoms.length;
    const v = new Float64Array(3 * n);
    const dt0 = Math.min(Math.max(this.timeStep, 0.1), 1.0);
    const dtMax = 10 * dt0;
    const dtMin = 0.02 * dt0;
    let dt = dt0;
    let alpha = CrystalDynamics.FIRE_ALPHA_START;
    let stepsSinceUphill = 0;
    const maxDispSq = maxDisplacement * maxDisplacement;
    let bestEnergy = Number.POSITIVE_INFINITY;
    const bestHistory = new Float64Array(stallWindow);
    // Rigid molecules (SHAKE constraints) stay rigid: the positions before each
    // step are kept so the constraint projection can use the old bond vectors.
    const rigid = this.constraints !== undefined && this.constraints.length > 0;
    const prev: Vector3[] = rigid ? this.atoms.map((a) => a.position.clone()) : [];
    let steps = 0;
    for (let s = 0; s < maxSteps; s++) {
      steps = s + 1;
      this.pairInteractions.checkNeighborList();
      for (const vs of this.virtualSites) vs.updatePosition();
      this.potentialEnergy = 0;
      this.calculateForce();
      // Largest force in eV/Å (acceleration × mass / GF) and the power F·v.
      let maxForce = 0;
      let power = 0;
      let vNormSq = 0;
      let fNormSq = 0;
      for (let i = 0; i < n; i++) {
        const a = this.atoms[i];
        if (a.fixed || !a.force) continue;
        const f = a.force;
        maxForce = Math.max(maxForce, (f.length() * a.mass) / GF_CONVERSION_CONSTANT);
        const k = 3 * i;
        power += f.x * v[k] + f.y * v[k + 1] + f.z * v[k + 2];
        vNormSq += v[k] * v[k] + v[k + 1] * v[k + 1] + v[k + 2] * v[k + 2];
        fNormSq += f.x * f.x + f.y * f.y + f.z * f.z;
      }
      if (maxForce < forceTol) break;
      // Energy test: FIRE's energy is not monotonic, so track the best energy
      // seen and stop once it has stopped improving over the window (the
      // strain left is then below ~1 meV/atom, a kelvin or two of heating on
      // Play) provided no single force is still large.
      bestEnergy = Math.min(bestEnergy, this.potentialEnergy);
      bestHistory[s % stallWindow] = bestEnergy;
      if (
        s >= Math.max(stallWindow, CrystalDynamics.FIRE_STALL_MIN_STEPS) &&
        maxForce < CrystalDynamics.FIRE_FORCE_LOOSE
      ) {
        const earlier = bestHistory[(s + 1) % stallWindow];
        if (earlier - bestEnergy < stallTol * stallWindow * this.movableCount) break;
      }
      if (power > 0) {
        stepsSinceUphill++;
        if (stepsSinceUphill > CrystalDynamics.FIRE_N_MIN) {
          dt = Math.min(dt * CrystalDynamics.FIRE_DT_GROW, dtMax);
          alpha *= CrystalDynamics.FIRE_ALPHA_DECAY;
        }
      } else {
        // Uphill: undo half the last displacement, stop, and shorten the step.
        stepsSinceUphill = 0;
        for (let i = 0; i < n; i++) {
          const a = this.atoms[i];
          if (a.fixed || !a.force) continue;
          const k = 3 * i;
          a.position.x -= 0.5 * dt * v[k];
          a.position.y -= 0.5 * dt * v[k + 1];
          a.position.z -= 0.5 * dt * v[k + 2];
        }
        v.fill(0);
        vNormSq = 0;
        dt = Math.max(dt * CrystalDynamics.FIRE_DT_SHRINK, dtMin);
        alpha = CrystalDynamics.FIRE_ALPHA_START;
      }
      // Semi-implicit Euler: v += dt·a …
      vNormSq = 0;
      for (let i = 0; i < n; i++) {
        const a = this.atoms[i];
        if (a.fixed || !a.force) continue;
        const k = 3 * i;
        v[k] += dt * a.force.x;
        v[k + 1] += dt * a.force.y;
        v[k + 2] += dt * a.force.z;
        vNormSq += v[k] * v[k] + v[k + 1] * v[k + 1] + v[k + 2] * v[k + 2];
      }
      // … then steer v toward the force direction: v ← (1−α)v + α|v|F̂.
      const mix = fNormSq > 0 ? (alpha * Math.sqrt(vNormSq)) / Math.sqrt(fNormSq) : 0;
      if (rigid) for (let i = 0; i < n; i++) prev[i].copy(this.atoms[i].position);
      for (let i = 0; i < n; i++) {
        const a = this.atoms[i];
        if (a.fixed || !a.force) continue;
        const f = a.force;
        const k = 3 * i;
        v[k] = (1 - alpha) * v[k] + mix * f.x;
        v[k + 1] = (1 - alpha) * v[k + 1] + mix * f.y;
        v[k + 2] = (1 - alpha) * v[k + 2] + mix * f.z;
        let dx = dt * v[k];
        let dy = dt * v[k + 1];
        let dz = dt * v[k + 2];
        const d2 = dx * dx + dy * dy + dz * dz;
        if (d2 > maxDispSq) {
          const sc = maxDisplacement / Math.sqrt(d2);
          dx *= sc;
          dy *= sc;
          dz *= sc;
          v[k] *= sc;
          v[k + 1] *= sc;
          v[k + 2] *= sc;
        }
        a.position.x += dx;
        a.position.y += dy;
        a.position.z += dz;
      }
      // Project rigid molecules back onto their constraints (the velocity
      // corrections SHAKE makes land in atom.velocity, zeroed below).
      if (rigid) this.constraints!.shake(this.atoms, prev, dt);
    }
    // Settle the virtual sites on the relaxed geometry, zero all motion,
    // and re-anchor the reset baseline so a later Reset returns to the
    // relaxed structure rather than the unrelaxed input.
    for (const vs of this.virtualSites) vs.updatePosition();
    for (const a of this.atoms) {
      a.velocity?.set(0, 0, 0);
      a.initialPosition?.copy(a.position);
      a.initialVelocity?.set(0, 0, 0);
    }
    this.indexOfStep = 0;
    this.updateKineticEnergy();
    return steps;
  }

  /** FIRE parameters (Bitzek et al. 2006 defaults) and the convergence tests. */
  private static readonly FIRE_N_MIN = 5;
  private static readonly FIRE_DT_GROW = 1.1;
  private static readonly FIRE_DT_SHRINK = 0.5;
  private static readonly FIRE_ALPHA_START = 0.1;
  private static readonly FIRE_ALPHA_DECAY = 0.99;
  /** Converged: largest force below this, eV/Å. */
  private static readonly FIRE_FORCE_TOL = 0.01;
  /**
   * No stall exit before this many steps: FIRE starts from rest with a short
   * step, so the energy barely moves in its first window even on a structure
   * that is far from relaxed.
   */
  private static readonly FIRE_STALL_MIN_STEPS = 40;
  /** Stalled: best energy improving by less than this per movable atom per step (eV) … */
  private static readonly FIRE_STALL_TOL = 5e-6;
  /** … averaged over this many steps, with the largest force below this (eV/Å). */
  private static readonly FIRE_STALL_WINDOW = 20;
  private static readonly FIRE_FORCE_LOOSE = 0.1;

  /**
   * Maxwell-Boltzmann velocity assignment.
   *
   * Unlike the molecule MD's version, the per-atom velocity scale is
   * mass-weighted: each velocity component has variance kT/m, so the
   * speed scale goes as 1/√m. The molecule MD omits this — its atoms
   * span a narrow mass range and the instant thermostat papers over
   * it — but a crystal with O (16) and H (1) has a 16:1 mass ratio,
   * and a mass-blind assignment hands every oxygen 16× the kinetic
   * energy equipartition allows. That dumps the molecules' kinetic
   * energy almost entirely into center-of-mass translation (the O is
   * 16/18 of a water's mass), which then leaks into the H-bond modes
   * and shakes the lattice apart. The final exact rescale fixes the
   * absolute temperature, so only the 1/√m *ratio* has to be right.
   */
  assignTemperature(temperature: number) {
    const n = this.atoms.length;
    if (n === 0) return;
    if (this.movableCount === 0) return;
    if (temperature < 0) temperature = 0;
    const tmp = Math.sqrt(temperature) * VT_CONVERSION_CONSTANT;
    let sumVx = 0;
    let sumVy = 0;
    let sumVz = 0;
    let sumMass = 0;
    for (const a of this.atoms) {
      if (a.fixed) continue;
      const s = tmp / Math.sqrt(a.mass);
      // A real standard-normal per component — Maxwell-Boltzmann. (The
      // legacy ModelUtil.nextGaussian() is NOT a Gaussian; it returns
      // ~0.5, which seeds every atom with nearly the same velocity.)
      a.velocity.x = s * ModelUtil.nextStandardNormal();
      a.velocity.y = s * ModelUtil.nextStandardNormal();
      a.velocity.z = s * ModelUtil.nextStandardNormal();
      sumVx += a.velocity.x * a.mass;
      sumVy += a.velocity.y * a.mass;
      sumVz += a.velocity.z * a.mass;
      sumMass += a.mass;
    }
    if (sumMass > ZERO_TOLERANCE && this.movableCount > 1) {
      sumVx /= sumMass;
      sumVy /= sumMass;
      sumVz /= sumMass;
      for (const a of this.atoms) {
        if (a.fixed) continue;
        a.velocity.x -= sumVx;
        a.velocity.y -= sumVy;
        a.velocity.z -= sumVz;
      }
    }
    // Exact rescale to the target temperature. assignTemperature is the
    // one-shot velocity seeding; the per-step setTemperature() is a
    // gentle Berendsen rescale (only ~0.25%/step) and can't be relied
    // on to set the initial temperature, so do it precisely here.
    this.updateKineticEnergy();
    const current = this.getCurrentTemperature();
    if (current > ZERO_TOLERANCE) {
      this.rescaleVelocities(Math.sqrt(temperature / current));
    }
  }

  setTemperature(temperature: number) {
    if (this.atoms.length === 0) return;
    if (this.movableCount === 0) return;
    if (temperature < ZERO_TOLERANCE) temperature = 0;
    this.updateKineticEnergy();
    const current = this.getCurrentTemperature();
    // Cold start with a positive target: seed velocities. assignTemperature
    // sets the temperature exactly (and DOF-correctly) on its own, so the
    // Berendsen step below would be a no-op — return early.
    if (current < ZERO_TOLERANCE) {
      if (temperature > ZERO_TOLERANCE) this.assignTemperature(temperature);
      return;
    }
    const dtOverTau = this.thermostatTau > 0 ? this.timeStep / this.thermostatTau : 1;
    if (this.thermostat === 'csvr') {
      // CSVR — stochastic velocity rescaling (Bussi-Donadio-Parrinello,
      // 2007). Resample the kinetic energy toward the canonical
      // distribution, then rescale velocities to match. Working in KE
      // space, the target KE is the current KE scaled by T_target/T —
      // exact, and it sidesteps inverting the KE↔T conversion constant.
      const targetKE = this.kineticEnergy * (temperature / current);
      const keNew = csvrNewKineticEnergy(this.kineticEnergy, targetKE, this.degreesOfFreedom(), dtOverTau);
      this.rescaleVelocities(Math.sqrt(Math.max(keNew, 0) / this.kineticEnergy));
      return;
    }
    // Berendsen velocity rescale:
    //   ratio = sqrt(1 + (dt/τ) · (T_target/T_current − 1))
    // For dt/τ = 1 this is the molecule MD's instant rescale; the
    // weak-coupling default (dt/τ ≪ 1) lets the canonical equipartition
    // reassert itself over time rather than locking KE at the target
    // each step and starving the vibrational modes. We also clamp the
    // argument under the sqrt at a tiny positive value so a single huge
    // KE spike can't make the ratio imaginary. Both temperatures here
    // come from getCurrentTemperature(), so the rescale targets the
    // true (DOF-correct) temperature for rigid molecules too.
    const arg = 1 + dtOverTau * (temperature / current - 1);
    this.rescaleVelocities(Math.sqrt(Math.max(arg, 1e-6)));
  }

  /**
   * Install resolved thermostat groups (an empty list restores the plain
   * whole-system thermostat). A rigid molecule (SHAKE constraints) must be
   * thermostatted as a whole — rescaling half of one fights the
   * constraints — so membership is widened to every atom a constraint
   * chain connects to a member. The ungrouped set is recomputed here.
   */
  setThermostatGroups(groups: ResolvedThermostatGroup[]): void {
    const n = this.atoms.length;
    let widened = groups;
    if (this.constraints && this.constraints.length > 0 && groups.length > 0) {
      // Union-find over the constraint pairs → the rigid-molecule partition.
      const parent = new Int32Array(n);
      for (let i = 0; i < n; i++) parent[i] = i;
      const find = (i: number): number => {
        while (parent[i] !== i) {
          parent[i] = parent[parent[i]];
          i = parent[i];
        }
        return i;
      };
      for (const c of this.constraints.constraints) {
        const a = find(c.i);
        const b = find(c.j);
        if (a !== b) parent[a] = b;
      }
      const claimed = new Uint8Array(n);
      widened = [];
      for (const g of groups) {
        const roots = new Set<number>();
        for (const i of g.indices) roots.add(find(i));
        const indices: number[] = [];
        for (let i = 0; i < n; i++) {
          if (claimed[i] || this.atoms[i].removed || this.atoms[i].fixed) continue;
          if (roots.has(find(i))) {
            indices.push(i);
            claimed[i] = 1;
          }
        }
        if (indices.length > 0) widened.push({ ...g, indices });
      }
    }
    this.thermostatGroups = widened;
    const inGroup = new Uint8Array(n);
    for (const g of widened) for (const i of g.indices) inGroup[i] = 1;
    const rest: number[] = [];
    for (let i = 0; i < n; i++) if (!inGroup[i] && !this.atoms[i].removed) rest.push(i);
    this.ungroupedIndices = rest;
    for (const id of [...this.thermostatGroupHeatById.keys()]) {
      if (!widened.some((g) => g.id === id)) this.thermostatGroupHeatById.delete(id);
    }
  }

  private addGroupHeat(id: string, heatEv: number): void {
    if (!Number.isFinite(heatEv)) return;
    this.thermostatGroupHeatById.set(id, (this.thermostatGroupHeatById.get(id) ?? 0) + heatEv);
  }

  /**
   * Thermostat a SUBSET of atoms to `temperature` with the lab's algorithm
   * (Berendsen weak coupling, or CSVR when selected), touching only those
   * atoms. Degrees of freedom are 3 per movable member — no center-of-mass
   * or constraint correction, which a partial set does not warrant. A
   * subset at rest with a positive setpoint is seeded with Maxwell–Boltzmann
   * velocities first (CSVR cannot grow a zero kinetic energy).
   */
  thermostatSubset(indices: readonly number[], temperature: number, heatId?: string): void {
    if (indices.length === 0) return;
    if (temperature < ZERO_TOLERANCE) temperature = 0;
    let ke = 0;
    let n = 0;
    for (const i of indices) {
      const a = this.atoms[i];
      if (a.fixed || a.removed) continue;
      ke += a.getKineticEnergy();
      n++;
    }
    if (n === 0) return;
    const dof = 3 * n;
    const targetKE = (dof * temperature) / (2 * UNIT_EV_OVER_KB);
    if (ke < ZERO_TOLERANCE) {
      if (temperature <= 0) return;
      // Seed: σ_v = VT·√(T/m) per component, then an exact rescale to T.
      let seeded = 0;
      for (const i of indices) {
        const a = this.atoms[i];
        if (a.fixed || a.removed) continue;
        const s = (VT_CONVERSION_CONSTANT * Math.sqrt(temperature)) / Math.sqrt(a.mass);
        a.velocity.set(
          s * ModelUtil.nextStandardNormal(),
          s * ModelUtil.nextStandardNormal(),
          s * ModelUtil.nextStandardNormal(),
        );
        seeded += a.getKineticEnergy();
      }
      if (seeded > 0) this.rescaleSubset(indices, Math.sqrt(targetKE / seeded));
      if (heatId) this.addGroupHeat(heatId, targetKE);
      return;
    }
    const dtOverTau = this.thermostatTau > 0 ? this.timeStep / this.thermostatTau : 1;
    if (this.thermostat === 'csvr') {
      const keNew = Math.max(csvrNewKineticEnergy(ke, targetKE, dof, dtOverTau), 0);
      this.rescaleSubset(indices, Math.sqrt(keNew / ke));
      if (heatId) this.addGroupHeat(heatId, keNew - ke);
      return;
    }
    const arg = Math.max(1 + dtOverTau * (targetKE / ke - 1), 1e-6);
    this.rescaleSubset(indices, Math.sqrt(arg));
    if (heatId) this.addGroupHeat(heatId, ke * (arg - 1));
  }

  private rescaleSubset(indices: readonly number[], ratio: number): void {
    for (const i of indices) {
      const a = this.atoms[i];
      if (a.fixed || a.removed) continue;
      a.velocity.multiplyScalar(ratio);
    }
  }

  /** Live per-group readout: setpoint, measured temperature, member count. */
  groupTemperatures(): {
    id: string;
    name: string;
    setpointK: number;
    heatEv: number;
    temperatureK: number;
    atomCount: number;
  }[] {
    return this.thermostatGroups.map((g) => {
      const t = groupTemperature(this.atoms, g.indices, (a) => (a as Atom).getKineticEnergy());
      return {
        id: g.id,
        name: g.name,
        setpointK: g.temperature,
        heatEv: this.thermostatGroupHeatById.get(g.id) ?? 0,
        ...t,
      };
    });
  }

  rescaleVelocities(ratio: number) {
    for (const a of this.atoms) {
      if (a.fixed) continue;
      a.velocity.multiplyScalar(ratio);
    }
  }

  /**
   * Constant-POWER heating: energy (eV, whole system) added to the kinetic
   * energy every step by a uniform velocity rescale, with the heat bath OFF.
   * This is the microcanonical heating curve — temperature is an outcome, not
   * a setpoint, so it genuinely stalls while latent heat is absorbed. Zero
   * (the default) means plain NVE. Negative values cool. Used by the crystal
   * agent's runHeatingCurve; nothing else sets it.
   */
  heatPerStepEv = 0;

  private injectHeat() {
    if (this.heatPerStepEv === 0 || this.movableCount === 0) return;
    this.updateKineticEnergy();
    if (this.kineticEnergy < ZERO_TOLERANCE) return;
    const target = this.kineticEnergy + this.heatPerStepEv;
    if (target <= 0) return;
    this.rescaleVelocities(Math.sqrt(target / this.kineticEnergy));
  }

  // Heat / cool: rescale the kinetic energy by (1 + percent/100). The thermostat
  // SETPOINT is not touched here — it belongs to the project state, which the
  // lab controls scale (and clamp) themselves and push to the engine on every
  // batch. (The old version bumped heatBath.temperature and then checked it
  // against MAXIMUM_TEMPERATURE — but increaseByPercentage already clamps to
  // that maximum, so the check could never fire and heating was unlimited: Heat
  // at 4900 K gave 5880 K. It also seeded a lab at rest at a fixed 100 K, so
  // Cool at rest HEATED it to 80 K and Heat before the first Play ignored the
  // set temperature.)
  changeTemperature(percent: number) {
    if (this.atoms.length === 0) return;
    percent *= 0.01;
    const ratio = Math.max(0, 1 + percent);
    this.updateKineticEnergy();
    if (this.kineticEnergy < ZERO_TOLERANCE) {
      // Nothing to scale. Cooling a lab at rest is a no-op; heating one seeds it
      // at the bath setpoint first (the same temperature the first Play would
      // seed), so the heat lands on top of the set temperature.
      if (ratio <= 1) return;
      this.assignTemperature(this.heatBath.temperature);
      this.updateKineticEnergy();
      if (this.kineticEnergy < ZERO_TOLERANCE) return;
    }
    // Refuse a heat step that would carry the ACTUAL kinetic temperature past
    // the cap (the setpoint clamp in the store is separate).
    if (ratio > 1 && this.getCurrentTemperature() * ratio > HeatBath.MAXIMUM_TEMPERATURE) return;
    this.rescaleVelocities(Math.sqrt(ratio));
    this.updateKineticEnergy();
  }

  updateKineticEnergy() {
    this.kineticEnergy = 0;
    for (const a of this.atoms) {
      this.kineticEnergy += a.getKineticEnergy();
    }
  }

  /**
   * Thermodynamic degrees of freedom — the divisor behind the kinetic
   * temperature. An unconstrained movable atom contributes 3; each rigid
   * SHAKE/RATTLE distance constraint removes exactly 1; and 3 more are
   * removed for the center-of-mass translation that assignTemperature()
   * zeroes (total momentum is conserved thereafter).
   *
   * This is the fix for rigid water: a 3-atom water held by 3 distance
   * constraints has 6 DOF, not 9. A flat 3·N count made both the
   * temperature readout AND the Berendsen setpoint 3/2× too low — a run
   * labeled "100 K" was physically at 150 K. Unconstrained crystals
   * (metals, ionic, covalent) are unaffected: with no constraints the
   * count is the usual 3·N − 3.
   */
  degreesOfFreedom(): number {
    // Only constraints that bind a MOVABLE atom remove a degree of freedom:
    // a fixed molecule contributes neither its 9 nor its −3. Counting every
    // constraint made the thermometer read 2× high with half a rigid-water
    // box fixed (an ice/water coexistence setup), so the thermostat held the
    // free half at half its setpoint (measured 2026-10-07).
    //
    // And only INDEPENDENT constraints count. A rigid molecule of n atoms
    // has 6 degrees of freedom left whatever its constraint list holds, so
    // at most 3n − 6 of its distance constraints are independent; the
    // over-determined sets SHAKE needs for stability (10 on a tetrahedron's
    // 9, 21 on an octahedron's 15) must not be subtracted in full. Taken
    // raw, SF₆'s 21 made every molecule worth 0 — the whole box read as
    // one degree of freedom, the thermostat drove it to ~0 K at any
    // setpoint, and a "400 K" slab stayed a perfect crystal (2026-10-13).
    //
    // A LINEAR rigid molecule (N₂, CO₂, N₂O, HCN, C₂H₂) keeps 5, not 6: the
    // rotation about its own axis moves no atom. Its distance constraints
    // are degenerate at collinearity (the O–O pin fixes the O=C=O angle only
    // to second order), so the raw count 3n − 6 = 3 for CO₂ left it 6 and a
    // pinned chabazite holding one CO₂ read its temperature 6/5 low.
    let constraintCount = 0;
    if (this.constraints) {
      for (const g of this.constraintMolecules()) {
        if (!g.movable) continue;
        constraintCount += g.linear ? 3 * g.atoms - 5 : Math.min(g.constraints, 3 * g.atoms - 6);
      }
    }
    // Total momentum is conserved — and its 3 components are not thermal —
    // only when every atom moves. A pinned framework (a frozen zeolite with
    // free guests) absorbs momentum, so nothing is subtracted: the 3 atoms of
    // one rigid CO₂ in a pinned cage are 5 degrees of freedom, not 2.
    const com = this.movableCount === this.atoms.length ? 3 : 0;
    const dof = 3 * this.movableCount - constraintCount - com;
    return dof > 0 ? dof : 1;
  }

  // Molecules as the constraint list defines them (atoms joined by SHAKE
  // pairs), with their atom and constraint counts; recomputed only when the
  // constraint list changes. `movable` follows the atoms' current fixed
  // flags, which countMovables() may change between calls.
  private constraintMoleculesCache: {
    set: ConstraintSet;
    count: number;
    groups: number[][];
    pairs: number[];
    linear: boolean[];
  } | null = null;
  private constraintMolecules(): { atoms: number; constraints: number; movable: boolean; linear: boolean }[] {
    const set = this.constraints!;
    const list = set.constraints;
    let cache = this.constraintMoleculesCache;
    if (!cache || cache.set !== set || cache.count !== list.length) {
      const parent = this.atoms.map((_, i) => i);
      const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i])));
      for (const c of list) parent[find(c.i)] = find(c.j);
      const index = new Map<number, number>();
      const groups: number[][] = [];
      const pairs: number[] = [];
      for (const c of list) {
        const r = find(c.i);
        let g = index.get(r);
        if (g === undefined) {
          g = groups.length;
          index.set(r, g);
          groups.push([]);
          pairs.push(0);
        }
        pairs[g]++;
      }
      for (let i = 0; i < this.atoms.length; i++) {
        const g = index.get(find(i));
        if (g !== undefined) groups[g].push(i);
      }
      // Collinear as built (the constraints keep it so): every atom within
      // 0.05 Å of the line through the two farthest apart.
      const linear = groups.map((atoms) => {
        if (atoms.length <= 2) return true;
        let a = atoms[0];
        let b = atoms[1];
        let far = -1;
        for (const i of atoms)
          for (const j of atoms) {
            const d = this.atoms[i].position.distanceToSquared(this.atoms[j].position);
            if (d > far) {
              far = d;
              a = i;
              b = j;
            }
          }
        if (far <= 0) return false;
        const axis = this.atoms[b].position.clone().sub(this.atoms[a].position).normalize();
        const v = new Vector3();
        for (const i of atoms) {
          v.subVectors(this.atoms[i].position, this.atoms[a].position);
          const along = v.dot(axis);
          if (v.lengthSq() - along * along > 0.05 * 0.05) return false;
        }
        return true;
      });
      cache = this.constraintMoleculesCache = { set, count: list.length, groups, pairs, linear };
    }
    return cache.groups.map((atoms, g) => ({
      atoms: atoms.length,
      constraints: cache!.pairs[g],
      movable: atoms.some((i) => !this.atoms[i].fixed),
      linear: cache!.linear[g],
    }));
  }

  getCurrentTemperature(): number {
    if (this.atoms.length === 0) return 0;
    // Equipartition: ⟨KE⟩ = (N_dof/2)·k_B·T  ⇒  T = 2·KE / (N_dof·k_B).
    // UNIT_EV_OVER_KB is 1/k_B in K/eV, so the kelvin form is
    // T = 2·KE·UNIT_EV_OVER_KB / N_dof.
    return (this.kineticEnergy * UNIT_EV_OVER_KB * 2) / this.degreesOfFreedom();
  }

  /**
   * Mean kinetic energy per atom at thermal equilibrium at `temperature`
   * — the reference the crystal viewer's KE color map paints as "green"
   * (an atom exactly at the set temperature). Inverts
   * getCurrentTemperature(): KE_total = T·N_dof / (2·UNIT_EV_OVER_KB).
   */
  referenceKineticEnergyPerAtom(temperature: number): number {
    const n = this.presentAtomCount();
    if (n === 0) return 0;
    return (temperature * this.degreesOfFreedom()) / (2 * UNIT_EV_OVER_KB * n);
  }

  /**
   * Atoms that are actually in the system: every array entry except those a
   * context-menu Cut flagged `removed` (the entry stays so indices are stable,
   * but the atom neither moves nor interacts). The denominator for every
   * "per atom" readout and for the density — the raw array length counted cut
   * atoms, so cutting a molecule left the density unchanged and diluted the
   * per-atom energies. Pinned atoms ARE present: they carry potential energy
   * and mass like any other.
   */
  presentAtomCount(): number {
    let n = 0;
    for (const a of this.atoms) if (!a.removed) n++;
    return n;
  }

  getCurrentVolume(): number {
    // Periodic: the true cell volume (exact for skewed cells too).
    if (this.box) return this.box.volume;
    return this.container.lx * this.container.ly * this.container.lz;
  }

  getCurrentDensity(): number {
    if (this.atoms.length === 0) return 0;
    let mass = 0;
    for (const a of this.atoms) {
      if (a.removed) continue;
      mass += a.mass;
    }
    return (mass / this.getCurrentVolume()) * DENSITY_CONVERSION_CONSTANT;
  }

  getCurrentPressure(): number {
    if (this.atoms.length === 0) return 0;
    // Kinetic term over the MOBILE atoms only — T is normalized by the mobile
    // DOF (degreesOfFreedom), so N_total would over-count when atoms are fixed.
    return (
      (this.getCurrentTemperature() * this.movableCount * PRESSURE_CONVERSION_CONSTANT + this.getVirialPressure()) /
      this.getCurrentVolume()
    );
  }

  getVirialPressure(): number {
    if (this.atoms.length === 0) return 0;
    let w = 0;
    for (const a of this.atoms) {
      if (a.acceleration) {
        w += a.position.dot(a.acceleration) * a.mass;
      }
    }
    return (w * VIRIAL_CONVERSION_CONSTANT) / 3;
  }

  move() {
    if (this.movableCount === 0) return;
    if (this.indexOfStep % 10 === 0) this.pairInteractions.checkNeighborList();
    this.potentialEnergy = 0;
    if (this.constraints && this.constraints.length > 0) {
      this.moveConstrained();
    } else {
      this.moveFlexible();
    }
    if (this.thermostatGroups.length > 0) {
      // Groups: the global bath (if on) holds only the ungrouped atoms; each
      // group is driven to its own setpoint. Constant-power heating is a
      // whole-system protocol and is not combined with groups.
      if (this.heatBath.enabled) this.thermostatSubset(this.ungroupedIndices, this.heatBath.temperature);
      for (const g of this.thermostatGroups) this.thermostatSubset(g.indices, g.temperature, g.id);
    } else if (this.heatBath.enabled) {
      this.setTemperature(this.heatBath.temperature);
    } else {
      this.injectHeat();
    }
    this.applyBoundary();
    this.updateKineticEnergy();
    this.totalEnergy = this.kineticEnergy + this.potentialEnergy;
    this.indexOfStep++;
  }

  /**
   * Constrained velocity-Verlet — the rigid-molecule integrator.
   * Standard SHAKE/RATTLE: first half-kick + drift, SHAKE the positions
   * back onto the constraint manifold, recompute forces, second
   * half-kick, RATTLE the velocities. `atom.acceleration` carries a(t)
   * into the step; `atom.force` holds a(t+dt) after calculateForce
   * (the kernel already stores acceleration there, not raw force).
   */
  private moveConstrained() {
    const dt = this.timeStep;
    const halfDt = dt / 2;
    if (this.prevPositions.length !== this.atoms.length) {
      this.prevPositions = this.atoms.map(() => new Vector3());
    }
    for (let i = 0; i < this.atoms.length; i++) {
      const a = this.atoms[i];
      this.prevPositions[i].copy(a.position);
      if (a.fixed || !a.acceleration) continue;
      a.velocity.addScaledVector(a.acceleration, halfDt); // first half-kick
      a.position.addScaledVector(a.velocity, dt); // drift
    }
    this.constraints!.shake(this.atoms, this.prevPositions, dt);
    for (const vs of this.virtualSites) vs.updatePosition();
    this.calculateForce();
    this.clampForces();
    for (const a of this.atoms) {
      if (a.fixed || !a.force || !a.acceleration) continue;
      a.velocity.addScaledVector(a.force, halfDt); // second half-kick
      a.acceleration.copy(a.force); // carry a(t+dt) to the next step
    }
    this.constraints!.rattle(this.atoms);
  }

  /**
   * Flexible velocity-Verlet — the original predictor/corrector path,
   * for atomic crystals and non-water molecular crystals (harmonic
   * bonds). Delegates the integration to Atom.predict / Atom.correct.
   */
  private moveFlexible() {
    const dt2 = (this.timeStep * this.timeStep) / 2;
    const h = this.timeStep / 2;
    for (const a of this.atoms) {
      a.predict(this.timeStep, dt2);
    }
    // Virtual sites are not integrated — they ride along on their
    // parent atoms. Refresh their positions after predict() moves the
    // parents so the force pass sees them at the right geometry.
    for (const vs of this.virtualSites) {
      vs.updatePosition();
    }
    this.calculateForce();
    this.clampForces();
    for (const a of this.atoms) {
      a.correct(h);
    }
  }

  /**
   * Force clamp — last-line defense against integrator blow-up when a
   * freshly-loaded crystal has atoms in slightly bad positions. Normal
   * thermal forces are ~0.02 Å/fs²; capping at 1.0 Å/fs² is generous
   * for legitimate dynamics but stops a single bad pair from launching
   * an atom across the box in one step. With rigid water this should
   * never trigger — if it does, something upstream is still wrong.
   * Acceleration is stored in `atom.force` per the engine's convention.
   */
  private clampForces() {
    const MAX_ACCEL = 1.0;
    const MAX_ACCEL_SQ = MAX_ACCEL * MAX_ACCEL;
    for (const a of this.atoms) {
      const f = a.force;
      if (!f) continue;
      const m2 = f.x * f.x + f.y * f.y + f.z * f.z;
      if (m2 > MAX_ACCEL_SQ) {
        const s = MAX_ACCEL / Math.sqrt(m2);
        f.x *= s;
        f.y *= s;
        f.z *= s;
      }
    }
  }

  calculateForce() {
    if (this.atoms.length === 0) return;
    // Pair interactions (LJ + Coulomb today, Buckingham/Ewald later)
    // for the non-bonded forces, plus bonded forces for molecular
    // crystals. Bonded pairs are excluded from the pair kernel via
    // CrystalPairInteractions.skipPair so we don't double-count.
    this.potentialEnergy += this.pairInteractions.compute();
    // Harmonic bonded forces — only for the flexible path. Rigid
    // molecules get their shape from the SHAKE/RATTLE constraints
    // instead; applying harmonic bonds on top would fight the
    // constraints. (radialBonds is still passed to the pair kernel for
    // its skipPair intramolecular-exclusion regardless.)
    const rigid = this.constraints !== undefined && this.constraints.length > 0;
    if (!rigid) {
      if (this.radialBonds.length > 0) {
        for (const rb of this.radialBonds) {
          this.potentialEnergy += rb.compute();
        }
      }
      if (this.angularBonds.length > 0) {
        for (const ab of this.angularBonds) {
          this.potentialEnergy += ab.compute();
        }
      }
      if (this.torsionalBonds.length > 0) {
        for (const tb of this.torsionalBonds) {
          this.potentialEnergy += tb.compute();
        }
      }
    }
    for (const a of this.atoms) {
      if (a.restraint) {
        this.potentialEnergy += a.computeRestraint();
      }
      if (a.damp) {
        a.computeDamp();
      }
    }
    if (this.externalFields.length > 0) {
      for (const f of this.externalFields) {
        if (f.type === ExternalFieldType.Gravitational) {
          const mag = f.intensity * GRAVITY_CONVERSION_CONSTANT * 1.0e12;
          // Gravitational PE: F = m·g·ĝ matches −∇U for U = −m·g·(ĝ·r),
          // EV_CONVERTER turns amu·Å²/fs² into eV so the term sits in the
          // same units as the rest of `potentialEnergy`. Same fix as in
          // MolecularDynamics — without it ΔTotal drifts whenever gravity
          // is on.
          for (const a of this.atoms) {
            if (a.force) {
              a.force.add(this.gravitationDirection.clone().multiplyScalar(mag));
              this.potentialEnergy -= a.mass * mag * this.gravitationDirection.dot(a.position) * EV_CONVERTER;
            }
          }
        }
      }
    }
  }

  /**
   * Reflecting-wall boundary, for the finite (non-periodic) crystals.
   * Under periodic boundary conditions there are no walls — the
   * supercell tiles infinitely and minimum-image in the pair kernel
   * carries interactions across the cell faces — so this is skipped.
   */
  /**
   * Reflecting planes at z = ±zWallHalf for a cell that is periodic sideways
   * (a flexible membrane: the sheet runs through the periodic x/y faces, the gas
   * chambers above and below are closed by these planes). Null = none.
   */
  zWallHalf: number | null = null;

  applyBoundary() {
    if (this.box) {
      const hz = this.zWallHalf;
      if (hz === null) return;
      for (const a of this.atoms) {
        if (a.fixed || !a.velocity) continue;
        const radius = 0.5 * a.sigma;
        if (a.position.z > hz - radius) a.velocity.z = -Math.abs(a.velocity.z);
        else if (a.position.z < radius - hz) a.velocity.z = Math.abs(a.velocity.z);
      }
      return;
    }
    let radius = 0;
    const hx = this.container.lx / 2;
    const hy = this.container.ly / 2;
    const hz = this.container.lz / 2;
    for (const a of this.atoms) {
      if (a.fixed || !a.velocity) continue;
      radius = 0.5 * a.sigma;
      if (a.position.x > hx - radius) {
        a.velocity.x = -Math.abs(a.velocity.x);
      } else if (a.position.x < radius - hx) {
        a.velocity.x = Math.abs(a.velocity.x);
      }
      if (a.position.y > hy - radius) {
        a.velocity.y = -Math.abs(a.velocity.y);
      } else if (a.position.y < radius - hy) {
        a.velocity.y = Math.abs(a.velocity.y);
      }
      if (a.position.z > hz - radius) {
        a.velocity.z = -Math.abs(a.velocity.z);
      } else if (a.position.z < radius - hz) {
        a.velocity.z = Math.abs(a.velocity.z);
      }
    }
  }
}
